import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { languages } from '../i18n';
import { decodeHtmlEntities } from '../utils/decodeHtmlEntities';
import { useCartItems } from '../hooks/useCartItems';
import { useShippingQuote } from '../hooks/useShippingQuote';

const isDev = import.meta.env.DEV;
const debugLog = (...args: unknown[]) => {
  if (isDev) {
    console.log(...args);
  }
};

interface ProductsProps {
  countryCode: string;
  stateCode: string;
}

interface Product {
  id: number;
  parentId?: number;
  name: string;
  image: string;
  price: string;
  sku: string;
  varietat?: string;
  stockStatus: string;
}

interface ProductsResponse {
  products: Product[];
  language: string;
}

export default function Products({ countryCode, stateCode }: ProductsProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { items: cartItems, setQuantity, addOne } = useCartItems();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language);

  // Estats pel modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalType, setModalType] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  // Funció per mostrar modals
  const showModal = (title: string, message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setModalTitle(title);
    setModalMessage(message);
    setModalType(type);
    setModalOpen(true);
  };

  // Funció per obtenir el nom complet de l'idioma a partir del codi
  const getLanguageName = (code: string): string => {
    const language = languages.find(lang => lang.code === code);
    return language ? language.name : code;
  };

  const { data: productsResponse, isLoading, error } = useQuery<ProductsResponse>({
    queryKey: ['products', countryCode, i18n.language],
    queryFn: async () => {
      debugLog(`[Frontend] Sol·licitant productes en idioma: ${i18n.language}`);
      const response = await fetch(`/api/products/${countryCode}?lang=${i18n.language}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error(t('products.error'));
      }
      const data = await response.json();
      debugLog(`[Frontend] Rebuts productes en idioma: ${data.language}`);
      setCurrentLanguage(data.language);
      return data;
    },
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000
  });

  // Obtenim els productes de la resposta (memoitzat perquè no canviï de
  // referència a cada render i desestabilitzi els useMemo que en depenen)
  const products = useMemo(() => productsResponse?.products || [], [productsResponse]);

  // Font única de veritat pel càlcul d'enviament: es recalcula automàticament
  // en canviar el carret o els productes (aquesta pàgina no té camps de codi
  // postal/ciutat propis, així que el hook fa servir els valors provisionals).
  const { methods: shippingMethods } = useShippingQuote({
    countryCode,
    state: stateCode,
    postcode: '',
    city: '',
    items: cartItems,
    products,
    enabled: cartItems.length > 0
  });

  // El cost d'enviament es deriva sempre del primer mètode rebut: mai és un
  // valor "recordat" d'una consulta anterior.
  const shippingCost = useMemo(() => {
    if (shippingMethods.length === 0) return 0;
    const cost = parseFloat(String(shippingMethods[0].cost).replace(',', '.'));
    return isNaN(cost) ? 0 : cost;
  }, [shippingMethods]);

  const getQuantity = (productId: number) => cartItems.find(item => item.productId === productId)?.quantity || 0;

  const handleQuantityChange = (productId: number, quantity: number) => {
    setQuantity(productId, quantity);
  };

  const handleAddToCart = (productId: number) => {
    addOne(productId);
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      showModal(t('cart.error'), t('cart.emptyError'), 'error');
      return;
    }

    navigate('/cart');
  };

  // Filtrar productes per la cerca
  const filteredProducts = products?.filter(product => {
    if (!searchTerm) return true;
    return product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           product.sku.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Calcular subtotal dels productes al carret
  const subtotal = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const product = products?.find(p => p.id === item.productId);
      if (!product) return sum;

      // Assegurar-nos que el preu és un nombre vàlid
      const priceStr = product.price.replace(',', '.');
      const price = parseFloat(priceStr);
      if (isNaN(price)) return sum;

      return sum + (price * item.quantity);
    }, 0);
  }, [cartItems, products]);

  // Calcular total amb enviament
  const total = useMemo(() => subtotal + shippingCost, [subtotal, shippingCost]);

  if (isLoading) {
    return <div className="text-center py-8">{t('products.loading')}</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-600">{t('products.error')}</div>;
  }

  return (
    <div>
      {/* Modal per mostrar missatges */}
      <Modal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
      />

      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">{t('products.title')}</h2>
          <p className="text-sm text-gray-500">
            {t('products.languageConfirm')}: {getLanguageName(currentLanguage)}
          </p>
        </div>
        <div className="mt-4 md:mt-0 md:flex md:gap-4 md:items-center">
          {/* Camp de cerca */}
          <div className="relative w-full md:w-64">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder={t('products.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-md w-full"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 hidden sm:table-header-group">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('products.product')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('products.price')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('products.quantity')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredProducts?.map((product) => (
              <tr key={product.id} className="sm:table-row flex flex-col">
                <td className="px-6 py-4">
                  <div className="flex items-start min-w-0">
                    <div className="flex-shrink-0">                        <img
                        className="h-20 w-20 rounded object-contain bg-white border border-gray-200"
                        src={product.image || '/placeholder.png'}
                        alt={decodeHtmlEntities(product.name)}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder.png';
                        }}
                      />
                    </div>
                    <div className="ml-4 min-w-0 flex-1">
                      <div className="text-base font-medium text-gray-900 break-words">
                        {decodeHtmlEntities(product.name)}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {product.varietat ? decodeHtmlEntities(product.varietat) : '-'}
                      </div>
                      {product.parentId && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {t('products.variation')}
                        </div>
                      )}
                      
                      {/* Preu i quantitat en mòbil */}
                      <div className="sm:hidden mt-4 flex justify-between items-center">
                        <div className="text-sm font-medium text-gray-900">
                          {product.price}€
                        </div>
                        <div className="flex items-center">
                          <button 
                            onClick={() => handleQuantityChange(product.id, Math.max(0, getQuantity(product.id) - 1))}
                            className="px-2 py-1 bg-gray-200 rounded-l hover:bg-gray-300"
                            aria-label={t('products.decreaseQuantity')}
                          >
                            -
                          </button>
                          <span className="px-3 py-1 bg-gray-100">{getQuantity(product.id)}</span>
                          <button 
                            onClick={() => handleAddToCart(product.id)}
                            className="px-2 py-1 bg-gray-200 rounded-r hover:bg-gray-300"
                            aria-label={t('products.increaseQuantity')}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                  <div className="text-sm text-gray-900">
                    {product.price}€
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                  <div className="flex items-center">
                    <button 
                      onClick={() => handleQuantityChange(product.id, Math.max(0, getQuantity(product.id) - 1))}
                      className="px-2 py-1 bg-gray-200 rounded-l hover:bg-gray-300"
                      aria-label={t('products.decreaseQuantity')}
                    >
                      -
                    </button>
                    <span className="px-3 py-1 bg-gray-100">{getQuantity(product.id)}</span>
                    <button 
                      onClick={() => handleAddToCart(product.id)}
                      className="px-2 py-1 bg-gray-200 rounded-r hover:bg-gray-300"
                      aria-label={t('products.increaseQuantity')}
                    >
                      +
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            
            {filteredProducts?.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  {t('products.noProducts')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Resum del carret */}
      {cartItems.length > 0 && (
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-medium text-lg mb-2">{t('cart.summary')}</h3>
          <ul className="space-y-2">
            {cartItems.map((item) => {
                const product = products?.find(p => p.id === item.productId);
                if (!product) return null;

                // Assegurar-nos que el preu és un nombre vàlid
                const priceStr = product.price.replace(',', '.');
                const price = parseFloat(priceStr);
                const itemTotal = isNaN(price) ? 0 : price * item.quantity;

                return (
                  <li key={item.productId} className="flex justify-between">
                    <span>{decodeHtmlEntities(product.name)} x {item.quantity}</span>
                    <span className="font-medium">{itemTotal.toFixed(2)}€</span>
                  </li>
                );
              })}
          </ul>

          {/* Mostrar el mètode d'enviament si està disponible */}
          {shippingMethods.length > 0 && (
            <div className="mt-4 py-2 border-t">
              <div className="font-medium mb-2">{t('cart.shippingMethod')}</div>
              <div className="flex justify-between">
                <span>{decodeHtmlEntities(shippingMethods[0].title)}</span>
                <span className="font-medium">{parseFloat(shippingMethods[0].cost).toFixed(2)}€</span>
              </div>
            </div>
          )}
          
          <div className="mt-4 pt-2 border-t flex justify-between">
            <span className="font-bold">{t('cart.subtotal')}:</span>
            <span className="font-bold">{subtotal.toFixed(2)}€</span>
          </div>

          {shippingCost > 0 && (
            <div className="mt-1 flex justify-between">
              <span className="font-bold">{t('cart.shipping')}:</span>
              <span className="font-bold">{shippingCost.toFixed(2)}€</span>
            </div>
          )}

          <div className="mt-1 flex justify-between text-lg">
            <span className="font-bold">{t('cart.total')}:</span>
            <span className="font-bold">{total.toFixed(2)}€</span>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={handleCheckout}
              className="px-4 py-2 bg-black text-white rounded-md hover:bg-black"
            >
              {t('cart.checkout')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}